/**
 * Keel canonical domain model. Every persisted / transported type lives here as a zod schema plus an inferred type.
 * Conventions: money = integer minor units (Cents); units/prices = DecimalString; dates = ISODate (YYYY-MM-DD).
 * These schemas describe PERSISTED shapes. API input schemas must omit server-owned fields (ids, timestamps, passwordHash,
 * totpSecret, isAdmin, createdByUserId, Price.userId, fingerprint, importBatchId) and set them from the authenticated context.
 * Orchestrator-owned: propose changes in your report rather than editing.
 */
import { z } from 'zod';
// ───────────────────────────── primitives ─────────────────────────────
const noDefault = (s) => {
    const def = s._zod.def;
    if ((def.type === 'default' || def.type === 'prefault') && def.innerType)
        return noDefault(def.innerType);
    return def.type === 'nullable' && def.innerType ? noDefault(def.innerType).nullable() : s;
};
/** `schema.optional()` that leaves an absent key absent (zod 4 would fill the schema's default). */
export const optionalOf = (schema) => noDefault(schema).optional();
/**
 * `schema.partial()` for patches: keys the client didn't send stay absent. (zod 4 fills an optional field's default
 * even when its key is missing, which would reset every defaulted column a patch didn't mention.)
 */
export const patchOf = (schema) => {
    const partial = schema.partial();
    return partial.extend(Object.fromEntries(Object.entries(schema.shape).map(([k, f]) => [k, optionalOf(f)])));
};
export const IdSchema = z.string().min(1);
export const CentsSchema = z.number().int().safe();
export const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, 'decimal string');
export const ISODateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
export const ISOTimestampSchema = z.string().datetime({ offset: true });
export const CurrencyCodeSchema = z.string().length(3).transform((s) => s.toUpperCase());
export const CountryCodeSchema = z.enum(['AU', 'GB', 'US', 'NZ', 'CA', 'IE', 'DE', 'FR', 'SG', 'JP', 'ZA', 'OTHER']);
export const ColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/).optional();
const Timestamps = { createdAt: ISOTimestampSchema, updatedAt: ISOTimestampSchema };
// ───────────────────────────── users, entities, roles ─────────────────────────────
/** Daily check-in time until the user saves one (`notificationPrefs.dailyTime`): the web's default and the push hour. */
export const DEFAULT_DAILY_TIME = '19:00';
export const UserSchema = z.object({
    id: IdSchema,
    email: z.string().email(),
    displayName: z.string().min(1).max(120),
    passwordHash: z.string(), // scrypt: "scrypt$N$r$p$salt$hash" (base64)
    totpSecret: z.string().nullable().prefault(null),
    totpEnabled: z.boolean().prefault(false),
    totpLastStep: z.number().int().nullable().optional(), // server-owned: last accepted TOTP time-step (replay protection)
    locale: z.string().prefault('en-AU'),
    displayCurrency: CurrencyCodeSchema.prefault('AUD'),
    theme: z.enum(['system', 'light', 'dark']).prefault('system'),
    calmMode: z.boolean().prefault(false), // disables gamification / celebrations
    reducedMotion: z.boolean().prefault(false),
    privacyBlur: z.boolean().prefault(false),
    isAdmin: z.boolean().optional(), // true = may edit the shared securities catalogue and global prices (undefined = false)
    onboardingState: z.record(z.string(), z.unknown()).prefault({}),
    lastLoginAt: ISOTimestampSchema.nullable().prefault(null),
    // ── growth (migration 0011) ──
    isGuest: z.boolean().optional(), // anonymous "try first" workspace; claimed via auth.claimGuest
    guestExpiresAt: ISOTimestampSchema.nullable().optional(), // guest data is purged after this unless claimed
    emailVerifiedAt: ISOTimestampSchema.nullable().optional(), // non-blocking; gates only password reset and HMRC/bank connections
    plan: z.string().nullable().optional(), // paid plan id (plans.ts) set by the billing webhook; null = none
    planInterval: z.string().nullable().optional(), // 'monthly' | 'annual'
    planStatus: z.string().nullable().optional(), // Stripe subscription status
    trialEndsAt: ISOTimestampSchema.nullable().optional(), // Investor trial end (no card)
    stripeCustomerId: z.string().nullable().optional(),
    stripeSubscriptionId: z.string().nullable().optional(),
    googleSub: z.string().nullable().optional(), // linked Google identity (OIDC `sub`), set only from Settings → Security
    appleSub: z.string().nullable().optional(),
    ...Timestamps,
});
export const EntityTypeSchema = z.enum(['individual', 'household', 'smsf', 'trust', 'company', 'partnership', 'sole_trader', 'club']);
export const CgtRegimeSchema = z.enum(['current', 'au_2027_indexation']);
export const TaxProfileSchema = z.object({
    entityType: EntityTypeSchema,
    marginalRate: z.number().min(0).max(1).prefault(0.30), // for what-if estimates (AU 30% bracket post Stage 3); SMSF accumulation 0.15, pension 0
    medicareLevy: z.number().min(0).max(1).prefault(0.02),
    gstRegistered: z.boolean().prefault(false),
    gstBasis: z.enum(['cash', 'accruals']).prefault('cash'),
    gstPeriod: z.enum(['monthly', 'quarterly', 'annually']).prefault('quarterly'),
    vatScheme: z.enum(['standard', 'flat_rate']).optional(), // UK: flat-rate scheme pays a % of gross turnover (box 1) and reclaims no input VAT
    vatFlatRatePct: z.number().min(0).max(100).optional(), // e.g. 14.5
    vatRegistrationNumber: z.string().nullable().optional(), // UK VRN for HMRC MTD filing
    vatStagger: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable().optional(), // UK VAT quarter stagger: 1 = Mar/Jun/Sep/Dec, 2 = Apr/Jul/Oct/Jan, 3 = May/Aug/Nov/Feb
    invoiceNextNumber: z.number().int().nullable().optional(), // next invoice sequence number (server-managed)
    invoicePrefix: z.string().nullable().optional(), // e.g. "INV-"
    /** @deprecated ignored: the 2027 CGT reform (Act No. 49, 2026) applies automatically to CGT events from 1 July 2027. */
    cgtRegime: CgtRegimeSchema.prefault('current'),
    /** s112-155(3) election for assets held across 1 July 2027: market value (default) or the apportioning method. */
    cgt2027ValuationMethod: z.enum(['market_value', 'apportionment']).optional(),
    taxFileNumberMasked: z.string().nullable().prefault(null),
    abn: z.string().nullable().prefault(null),
    smsf: z.object({
        fundName: z.string(),
        pensionPhaseProportion: z.number().min(0).max(1).prefault(0), // exempt current pension income share
        investmentStrategyTargets: z.array(z.object({ assetClass: z.string(), targetPct: z.number(), minPct: z.number(), maxPct: z.number() })).prefault([]),
    }).nullable().prefault(null),
});
export const EntitySchema = z.object({
    id: IdSchema,
    name: z.string().min(1).max(120),
    type: EntityTypeSchema,
    country: CountryCodeSchema.prefault('AU'),
    baseCurrency: CurrencyCodeSchema.prefault('AUD'),
    locale: z.string().prefault('en-AU'),
    fyStartMonth: z.number().int().min(1).max(12).prefault(7),
    fyStartDay: z.number().int().min(1).max(31).prefault(1),
    taxProfile: TaxProfileSchema,
    color: ColorSchema,
    icon: z.string().optional(),
    address: z.string().nullable().optional(), // postal address printed on invoices and statements
    safetyBufferCents: z.number().int().min(0).max(100_000_000).prefault(0), // safe to spend keeps this back; transfer suggestions warn below it
    isArchived: z.boolean().prefault(false),
    isDemo: z.boolean().optional(), // server-owned: set by the demo loader; only demo entities may be bulk-deleted
    ...Timestamps,
});
export const RoleSchema = z.enum(['owner', 'editor', 'viewer', 'accountant']);
export const MembershipSchema = z.object({ id: IdSchema, userId: IdSchema, entityId: IdSchema, role: RoleSchema, createdAt: ISOTimestampSchema });
export const SmsfMemberSchema = z.object({
    id: IdSchema, entityId: IdSchema, name: z.string(), dateOfBirth: ISODateSchema,
    phase: z.enum(['accumulation', 'pension', 'ttr']), balanceCents: CentsSchema.prefault(0),
    ...Timestamps,
});
// ───────────────────────────── accounts ─────────────────────────────
export const AccountTypeSchema = z.enum([
    'transaction', 'savings', 'offset', 'term_deposit', 'cash', 'credit_card', 'loan', 'mortgage', 'hecs_help',
    'investment', 'superannuation', 'crypto', 'property', 'vehicle', 'other_asset', 'other_liability', 'receivable', 'payable',
]);
export const LIABILITY_TYPES = new Set(['credit_card', 'loan', 'mortgage', 'hecs_help', 'other_liability', 'payable']);
export const isLiability = (t) => LIABILITY_TYPES.has(t);
/**
 * Tax wrapper an account sits in. Anything but 'none' is tax-sheltered: its sales create no CGT events and its
 * acquisitions are never pooled with taxable holdings (UK ISA/LISA/SIPP, US 401(k)/IRA/Roth IRA, AU super).
 * An AU SMSF is an entity (taxed at 15 %), not a wrapper.
 */
export const TaxWrapperSchema = z.enum(['none', 'isa', 'lisa', 'sipp', 'pension', '401k', 'ira', 'roth_ira', 'super']);
export const isTaxSheltered = (w) => Boolean(w) && w !== 'none';
/** How a statement-fed account gets its transactions: the guided Amex CSV import, or any other bank's statement file. */
export const ImportSourceSchema = z.enum(['amex_csv', 'manual_statement']);
export const AccountSchema = z.object({
    id: IdSchema,
    entityId: IdSchema,
    name: z.string().min(1).max(120),
    type: AccountTypeSchema,
    currency: CurrencyCodeSchema,
    institution: z.string().nullable().prefault(null),
    accountNumberMasked: z.string().nullable().prefault(null),
    bsb: z.string().nullable().prefault(null),
    hin: z.string().nullable().prefault(null), // CHESS HIN / SRN for brokerage
    broker: z.string().nullable().prefault(null), // 'nabtrade' | 'commsec' | 'fpmarkets' | ...
    openingBalanceCents: CentsSchema.prefault(0),
    openingDate: ISODateSchema,
    interestRate: z.number().nullable().prefault(null), // annual, 0.0525
    creditLimitCents: CentsSchema.nullable().prefault(null),
    includeInNetWorth: z.boolean().prefault(true),
    taxWrapper: TaxWrapperSchema.prefault('none'),
    isClosed: z.boolean().prefault(false),
    color: ColorSchema,
    icon: z.string().optional(),
    notes: z.string().prefault(''),
    sortOrder: z.number().int().prefault(0),
    // ── property & valued assets (migration 0016) ──
    purchasePriceCents: CentsSchema.nullable().optional(), // property / vehicle / other_asset
    purchaseDate: ISODateSchema.nullable().optional(),
    address: z.string().max(300).nullable().optional(), // property address or asset description
    propertyUse: z.enum(['owner_occupied', 'investment']).nullable().optional(), // stored only (later: CGT main-residence hint)
    /** Loan / mortgage → the property (or other valued asset) it is secured against; many loans may share one asset. */
    securedByAccountId: IdSchema.nullable().optional(),
    /** Transaction / savings / offset account → the loan it offsets. */
    offsetForAccountId: IdSchema.nullable().optional(),
    /** Kept current by statement imports, not a bank feed (migration 0019): drives the monthly import reminder. */
    importSource: ImportSourceSchema.nullable().optional(),
    ...Timestamps,
});
/** Assets valued by the owner (Update value) rather than by transactions or market prices. */
export const VALUED_ASSET_TYPES = new Set(['property', 'vehicle', 'other_asset']);
/** Debts that can be secured against a valued asset. */
export const SECURED_LOAN_TYPES = new Set(['mortgage', 'loan', 'other_liability']);
/** Cash accounts that can offset a loan. */
export const OFFSET_SOURCE_TYPES = new Set(['transaction', 'savings', 'offset']);
// ───────────────────────────── categories, payees, tags ─────────────────────────────
export const CategoryKindSchema = z.enum(['income', 'expense', 'transfer']);
export const CategoryNatureSchema = z.enum(['essential', 'discretionary', 'savings', 'income', 'none']);
export const CategorySchema = z.object({
    id: IdSchema,
    entityId: IdSchema,
    parentId: IdSchema.nullable().prefault(null),
    name: z.string().min(1).max(80),
    kind: CategoryKindSchema,
    nature: CategoryNatureSchema.prefault('none'),
    icon: z.string().optional(),
    color: ColorSchema,
    isTaxDeductible: z.boolean().prefault(false),
    gstApplicable: z.boolean().prefault(false),
    isBudgetable: z.boolean().prefault(true),
    isHidden: z.boolean().prefault(false),
    isSystem: z.boolean().prefault(false), // e.g. "Uncategorised", "Transfer", "Opening balance"
    sortOrder: z.number().int().prefault(0),
    ...Timestamps,
});
export const PayeeSchema = z.object({
    id: IdSchema, entityId: IdSchema, name: z.string().min(1).max(160),
    aliases: z.array(z.string()).prefault([]), // raw descriptors that normalise to this payee
    defaultCategoryId: IdSchema.nullable().prefault(null),
    website: z.string().nullable().prefault(null),
    notes: z.string().prefault(''),
    ...Timestamps,
});
export const TagSchema = z.object({ id: IdSchema, entityId: IdSchema, name: z.string().min(1).max(60), color: ColorSchema, isProject: z.boolean().prefault(false), ...Timestamps });
// ───────────────────────────── transactions ─────────────────────────────
export const TransactionStatusSchema = z.enum(['pending', 'cleared', 'reconciled', 'void']);
export const TransactionOriginSchema = z.enum(['manual', 'import', 'recurring', 'rule', 'feed', 'system', 'valuation', 'receipt']); // valuation = non-cash revaluation of an asset; receipt = captured on the phone, merged into its bank row later
export const SplitSchema = z.object({
    id: IdSchema,
    categoryId: IdSchema.nullable(),
    amountCents: CentsSchema, // signed; sum of splits == transaction amount
    memo: z.string().prefault(''),
    tagIds: z.array(IdSchema).prefault([]),
    gstCents: CentsSchema.nullable().prefault(null), // tax component included in amount
    isTaxDeductible: z.boolean().nullable().prefault(null), // override of category flag
});
export const TransactionSchema = z.object({
    id: IdSchema,
    entityId: IdSchema,
    accountId: IdSchema,
    date: ISODateSchema,
    amountCents: CentsSchema, // signed in account currency: +deposit / −withdrawal
    payeeId: IdSchema.nullable().prefault(null),
    payeeRaw: z.string().prefault(''), // original descriptor from bank/import
    memo: z.string().prefault(''),
    reference: z.string().prefault(''), // cheque no., receipt no.
    status: TransactionStatusSchema.prefault('cleared'),
    origin: TransactionOriginSchema.prefault('manual'),
    splits: z.array(SplitSchema).min(1),
    transferId: IdSchema.nullable().prefault(null), // shared by both legs of a transfer
    transferAccountId: IdSchema.nullable().prefault(null),
    originalAmountCents: CentsSchema.nullable().prefault(null), // for FX: amount in originalCurrency
    originalCurrency: CurrencyCodeSchema.nullable().prefault(null),
    fxRate: z.number().nullable().prefault(null),
    recurringRuleId: IdSchema.nullable().prefault(null),
    importBatchId: IdSchema.nullable().prefault(null),
    fingerprint: z.string().nullable().prefault(null), // dedupe hash: account|date|amount|normalised descriptor
    attachmentIds: z.array(IdSchema).prefault([]),
    reconciliationId: IdSchema.nullable().prefault(null),
    isReviewed: z.boolean().prefault(true), // false = needs user confirmation (daily check-in)
    mcc: z.string().max(8).nullable().optional(), // card merchant category code from a bank feed (migration 0017; read by card rewards)
    ...Timestamps,
});
export const AttachmentSchema = z.object({
    id: IdSchema, entityId: IdSchema, fileName: z.string(), mimeType: z.string(), sizeBytes: z.number().int(),
    storagePath: z.string(), sha256: z.string(), createdAt: ISOTimestampSchema,
});
// rules engine
export const RuleConditionSchema = z.object({
    field: z.enum(['payeeRaw', 'memo', 'amount', 'accountId', 'reference']),
    op: z.enum(['contains', 'equals', 'startsWith', 'endsWith', 'regex', 'gt', 'lt', 'between']),
    value: z.string(),
    value2: z.string().optional(),
    caseSensitive: z.boolean().prefault(false),
});
export const RuleActionSchema = z.object({
    setPayeeId: IdSchema.optional(),
    setCategoryId: IdSchema.optional(),
    addTagIds: z.array(IdSchema).optional(),
    setMemo: z.string().optional(),
    splits: z.array(z.object({ categoryId: IdSchema, percent: z.number().optional(), amountCents: CentsSchema.optional() })).optional(),
    markReviewed: z.boolean().optional(),
    /** A payment to one of your own accounts (loan, card): linked as a transfer, its other side added to that account. */
    transferToAccountId: IdSchema.optional(),
});
export const RuleSchema = z.object({
    id: IdSchema, entityId: IdSchema, name: z.string(), isEnabled: z.boolean().prefault(true),
    match: z.enum(['all', 'any']).prefault('all'), conditions: z.array(RuleConditionSchema).min(1), actions: RuleActionSchema,
    priority: z.number().int().prefault(100), stopProcessing: z.boolean().prefault(false), hitCount: z.number().int().prefault(0),
    ...Timestamps,
});
// recurring / standing orders
export const FrequencySchema = z.enum(['daily', 'weekly', 'fortnightly', 'four_weekly', 'monthly', 'quarterly', 'half_yearly', 'yearly']);
export const RecurrenceSchema = z.object({
    frequency: FrequencySchema,
    interval: z.number().int().min(1).prefault(1),
    startDate: ISODateSchema,
    endDate: ISODateSchema.nullable().prefault(null),
    count: z.number().int().nullable().prefault(null),
    byMonthDay: z.number().int().min(-1).max(31).nullable().prefault(null), // -1 = last day of month
    byWeekday: z.array(z.number().int().min(0).max(6)).nullable().prefault(null),
    businessDayAdjust: z.enum(['none', 'previous', 'next']).prefault('none'),
});
export const RecurringRuleSchema = z.object({
    id: IdSchema, entityId: IdSchema, name: z.string(),
    recurrence: RecurrenceSchema,
    template: z.object({
        accountId: IdSchema, amountCents: CentsSchema, payeeId: IdSchema.nullable().prefault(null), memo: z.string().prefault(''),
        splits: z.array(SplitSchema.omit({ id: true })).min(1), transferAccountId: IdSchema.nullable().prefault(null),
    }),
    isVariableAmount: z.boolean().prefault(false),
    autoPost: z.boolean().prefault(false),
    remindDaysBefore: z.number().int().prefault(3),
    nextDueDate: ISODateSchema,
    lastPostedDate: ISODateSchema.nullable().prefault(null),
    skippedDates: z.array(ISODateSchema).prefault([]),
    isEnabled: z.boolean().prefault(true),
    ...Timestamps,
});
export const ReconciliationSchema = z.object({
    id: IdSchema, entityId: IdSchema, accountId: IdSchema, statementDate: ISODateSchema, statementBalanceCents: CentsSchema,
    reconciledBalanceCents: CentsSchema, status: z.enum(['in_progress', 'completed']), completedAt: ISOTimestampSchema.nullable().prefault(null),
    transactionIds: z.array(IdSchema).prefault([]), notes: z.string().prefault(''), ...Timestamps,
});
/**
 * A known-true balance for an account at the end of a day: from a bank feed (provider's current balance), a statement
 * (closing balance), the user ("Verify balance") or a completed reconciliation. `isLock` closes the period: editing or
 * deleting transactions dated on/before `asOf` in this account then needs confirmation.
 */
export const CheckpointSourceSchema = z.enum(['feed', 'statement', 'user', 'reconciliation']);
export const BalanceCheckpointSchema = z.object({
    id: IdSchema, entityId: IdSchema, accountId: IdSchema, asOf: ISODateSchema, balanceCents: CentsSchema, source: CheckpointSourceSchema,
    /** Feed checkpoints: which provider balance was recorded. */
    balanceKind: z.enum(['current', 'available', 'closing']).nullable().prefault(null),
    /** Import batch, feed connection or reconciliation id. */
    sourceRef: z.string().nullable().prefault(null),
    note: z.string().max(500).prefault(''), isLock: z.boolean().prefault(false), createdByUserId: IdSchema.nullable().prefault(null), ...Timestamps,
});
/** Original import file kept for the audit trail (content-addressed by sha256 per entity; bytes encrypted at rest). */
export const SourceFileSchema = z.object({
    id: IdSchema, entityId: IdSchema, sha256: z.string(), storagePath: z.string(), sizeBytes: z.number().int(), mimeType: z.string(),
    originalName: z.string(), uploadedAt: ISOTimestampSchema, firstDate: ISODateSchema.nullable().prefault(null), lastDate: ISODateSchema.nullable().prefault(null),
    ...Timestamps,
});
// ───────────────────────────── budgets & goals ─────────────────────────────
export const BudgetModeSchema = z.enum(['category', 'envelope', 'percent_plan']);
export const BudgetPeriodSchema = z.enum(['weekly', 'fortnightly', 'monthly', 'quarterly', 'yearly']);
export const BudgetLineSchema = z.object({
    id: IdSchema, categoryId: IdSchema, amountCents: CentsSchema, rollover: z.boolean().prefault(false),
    note: z.string().prefault(''),
});
export const BudgetSchema = z.object({
    id: IdSchema, entityId: IdSchema, name: z.string(), mode: BudgetModeSchema.prefault('category'), period: BudgetPeriodSchema.prefault('monthly'),
    anchorDate: ISODateSchema, lines: z.array(BudgetLineSchema), expectedIncomeCents: CentsSchema.prefault(0),
    percentPlan: z.object({ needs: z.number(), wants: z.number(), savings: z.number() }).nullable().prefault(null),
    isActive: z.boolean().prefault(true), payTipDismissed: z.boolean().prefault(false), ...Timestamps,
});
export const BudgetRuleKindSchema = z.enum(['fixed', 'last_period', 'average']);
/** How a budget line's amount is worked out from `effectiveFrom` on; the row with the latest date at or before a period applies. */
export const BudgetLineRuleSchema = z.object({
    budgetId: IdSchema, categoryId: IdSchema, effectiveFrom: ISODateSchema, rule: BudgetRuleKindSchema,
    months: z.union([z.literal(3), z.literal(6), z.literal(12)]).nullable(), amountCents: CentsSchema.nullable(),
});
/** effectiveFrom of the row that holds a line's original (pre-rule) amount. */
export const BASE_RULE_DATE = '0000-01-01';
export const GoalTypeSchema = z.enum(['savings', 'debt_payoff', 'net_worth', 'emergency_fund', 'purchase', 'custom']);
export const GoalSchema = z.object({
    id: IdSchema, entityId: IdSchema, name: z.string(), type: GoalTypeSchema, icon: z.string().optional(), color: ColorSchema,
    targetCents: CentsSchema, targetDate: ISODateSchema.nullable().prefault(null), startDate: ISODateSchema,
    linkedAccountIds: z.array(IdSchema).prefault([]), // progress derived from balances when set
    startingBalanceCents: CentsSchema.prefault(0),
    manualProgressCents: CentsSchema.prefault(0), // when no linked accounts
    plannedContributionCents: CentsSchema.prefault(0), contributionFrequency: FrequencySchema.prefault('monthly'),
    implementationIntention: z.string().prefault(''), // "When I get paid, I move $200 to this goal"
    milestonesHit: z.array(z.number()).prefault([]), // [25, 50, 75, 90, 100]
    status: z.enum(['active', 'achieved', 'paused', 'abandoned']).prefault('active'),
    achievedAt: ISOTimestampSchema.nullable().prefault(null),
    sortOrder: z.number().int().prefault(0),
    ...Timestamps,
});
// ───────────────────────────── securities & investments ─────────────────────────────
export const SecurityTypeSchema = z.enum(['share', 'etf', 'lic', 'managed_fund', 'bond', 'hybrid', 'option', 'warrant', 'crypto', 'cash', 'other']);
export const SecuritySchema = z.object({
    id: IdSchema,
    code: z.string().min(1).max(20), // VAS, BHP, AAPL
    exchange: z.string().min(1).max(12), // ASX, NYSE, NASDAQ, LSE, CXA, CRYPTO
    isin: z.string().nullable().prefault(null),
    name: z.string(),
    type: SecurityTypeSchema,
    currency: CurrencyCodeSchema,
    sector: z.string().nullable().prefault(null),
    assetClass: z.string().nullable().prefault(null), // 'au_equity' | 'intl_equity' | 'fixed_income' | 'property' | 'cash' | 'alternatives'
    country: z.string().nullable().prefault(null),
    isBenchmark: z.boolean().prefault(false),
    isAmit: z.boolean().prefault(false), // attribution managed investment trust (most AU ETFs)
    priceProvider: z.string().nullable().prefault(null),
    providerSymbol: z.string().nullable().prefault(null), // e.g. "VAS.AX" for Yahoo
    createdByUserId: IdSchema.nullable().optional(), // null/undefined = shared catalogue (admin/provider managed); set = private to that user
    isActive: z.boolean().prefault(true),
    ...Timestamps,
});
export const PriceSchema = z.object({
    securityId: IdSchema, date: ISODateSchema, closeCents: CentsSchema, // in security currency minor units
    open: CentsSchema.nullable().prefault(null), high: CentsSchema.nullable().prefault(null), low: CentsSchema.nullable().prefault(null),
    volume: z.number().nullable().prefault(null), source: z.string().prefault('manual'),
    userId: IdSchema.nullable().optional(), // null/undefined = global (provider) price; set = that user's manual override, which wins on the same date
});
export const FxRateSchema = z.object({ base: CurrencyCodeSchema, quote: CurrencyCodeSchema, date: ISODateSchema, rate: z.number().positive(), source: z.string().prefault('manual') });
export const TradeSideSchema = z.enum(['buy', 'sell']);
export const ParcelSelectionStrategySchema = z.enum([
    'fifo', 'lifo', 'highest_cost', 'lowest_cost', 'min_tax', 'max_loss', 'max_discounted_gain', 'manual',
]);
export const ParcelAllocationSchema = z.object({ parcelId: IdSchema, units: DecimalStringSchema });
export const TradeSchema = z.object({
    id: IdSchema, entityId: IdSchema, accountId: IdSchema, securityId: IdSchema,
    side: TradeSideSchema, date: ISODateSchema, settlementDate: ISODateSchema.nullable().prefault(null),
    units: DecimalStringSchema, priceCents: CentsSchema, // per unit, security currency
    brokerageCents: CentsSchema.prefault(0), feesCents: CentsSchema.prefault(0), gstCents: CentsSchema.prefault(0),
    grossCents: CentsSchema, // units × price
    netCents: CentsSchema, // buy: gross + costs (cash out); sell: gross − costs (cash in)
    currency: CurrencyCodeSchema, fxRate: z.number().nullable().prefault(null), // to entity base currency
    confirmationNo: z.string().nullable().prefault(null), broker: z.string().nullable().prefault(null),
    strategy: ParcelSelectionStrategySchema.nullable().prefault(null),
    allocations: z.array(ParcelAllocationSchema).prefault([]), // sells only
    cashTransactionId: IdSchema.nullable().prefault(null), // linked ledger transaction on the cash account
    importBatchId: IdSchema.nullable().prefault(null), fingerprint: z.string().nullable().prefault(null),
    notes: z.string().prefault(''), ...Timestamps,
});
export const ParcelAdjustmentSchema = z.object({
    date: ISODateSchema, sourceType: z.enum(['corporate_action', 'amit', 'manual', 'return_of_capital', 'indexation']), sourceId: IdSchema.nullable(),
    unitsDelta: DecimalStringSchema.prefault('0'), costBaseDeltaCents: CentsSchema.prefault(0), reducedCostBaseDeltaCents: CentsSchema.prefault(0), note: z.string().prefault(''),
});
export const ParcelSchema = z.object({
    id: IdSchema, entityId: IdSchema, accountId: IdSchema, securityId: IdSchema,
    acquiredDate: ISODateSchema, // CGT acquisition date (rollovers may preserve an earlier date)
    originalTradeId: IdSchema.nullable().prefault(null),
    originalUnits: DecimalStringSchema, remainingUnits: DecimalStringSchema,
    costBaseCents: CentsSchema, // for remaining units, incl. incidental costs, after adjustments
    reducedCostBaseCents: CentsSchema,
    isPre1999: z.boolean().prefault(false), // indexation method eligible (acquired before 21 Sep 1999)
    adjustments: z.array(ParcelAdjustmentSchema).prefault([]),
    closedDate: ISODateSchema.nullable().prefault(null),
    ...Timestamps,
});
export const CorporateActionTypeSchema = z.enum([
    'split', 'consolidation', 'bonus_issue', 'rights_issue', 'drp', 'return_of_capital', 'demerger', 'takeover_cash', 'takeover_scrip',
    'merger', 'code_change', 'name_change', 'delisting', 'amit_cost_base_adjustment', 'special_dividend', 'capital_call', 'unit_conversion',
]);
export const CorporateActionSchema = z.object({
    id: IdSchema, entityId: IdSchema.nullable().prefault(null), // null = market-wide event applying to all holders
    securityId: IdSchema, type: CorporateActionTypeSchema, date: ISODateSchema, exDate: ISODateSchema.nullable().prefault(null),
    /** type-specific parameters, validated by investments/corporateActions.ts:
     *  split/consolidation: { ratioFrom: number, ratioTo: number }       e.g. 1→2 split: from 1 to 2
     *  bonus_issue:        { ratioFor: number, ratioHeld: number }        1 for 10
     *  rights_issue:       { ratioFor, ratioHeld, priceCents, takenUp: boolean }
     *  drp:                { dividendId }  (units created via IncomeEvent.drpUnits)
     *  return_of_capital:  { perUnitCents }
     *  demerger:           { newSecurityId, unitsRatioFor, unitsRatioHeld, costBasePctToNew: number, newAcquisitionDateRule: 'original'|'event' }
     *  takeover_cash:      { perUnitCents }
     *  takeover_scrip:     { newSecurityId, ratioFor, ratioHeld, cashPerUnitCents?: number, rollover: boolean }
     *  code_change/name_change: { newCode?, newName? }
     *  amit_cost_base_adjustment: { netAmountPerUnitCents } (+ increases, − decreases)
     *  unit_conversion:    { newSecurityId, ratioFor, ratioHeld } */
    params: z.record(z.string(), z.unknown()),
    appliedAt: ISOTimestampSchema.nullable().prefault(null),
    source: z.enum(['manual', 'import', 'suggested']).prefault('manual'),
    notes: z.string().prefault(''), ...Timestamps,
});
export const IncomeEventSchema = z.object({
    id: IdSchema, entityId: IdSchema, accountId: IdSchema, securityId: IdSchema,
    kind: z.enum(['dividend', 'distribution', 'interest', 'special_dividend']),
    exDate: ISODateSchema.nullable().prefault(null), payDate: ISODateSchema, unitsHeld: DecimalStringSchema,
    cashPerUnitCents: z.number().prefault(0), // may be fractional cents → stored as number of cents (decimal ok)
    frankedCents: CentsSchema.prefault(0), unfrankedCents: CentsSchema.prefault(0), frankingCreditsCents: CentsSchema.prefault(0),
    frankingRate: z.number().prefault(0.30),
    foreignIncomeCents: CentsSchema.prefault(0), foreignTaxCents: CentsSchema.prefault(0),
    /** AMIT / trust components (cents): interest, otherIncome, discountedCapitalGain, cgtConcession, otherCapitalGain, taxDeferred, taxExempt, amitCostBaseNet */
    components: z.record(z.string(), CentsSchema).prefault({}),
    grossCents: CentsSchema, withholdingCents: CentsSchema.prefault(0), netCashCents: CentsSchema,
    isDrp: z.boolean().prefault(false), drpUnits: DecimalStringSchema.nullable().prefault(null),
    drpPriceCents: z.number().nullable().prefault(null), // may be fractional cents (ASX DRP prices have 4 dp): cost = units × price, rounded once
    currency: CurrencyCodeSchema, fxRate: z.number().nullable().prefault(null),
    cashTransactionId: IdSchema.nullable().prefault(null), importBatchId: IdSchema.nullable().prefault(null), fingerprint: z.string().nullable().prefault(null),
    notes: z.string().prefault(''), ...Timestamps,
});
export const CgtEventSchema = z.object({
    id: IdSchema, entityId: IdSchema, accountId: IdSchema, securityId: IdSchema, parcelId: IdSchema, tradeId: IdSchema.nullable(),
    corporateActionId: IdSchema.nullable().prefault(null),
    date: ISODateSchema, acquiredDate: ISODateSchema, units: DecimalStringSchema,
    proceedsCents: CentsSchema, costBaseCents: CentsSchema, reducedCostBaseCents: CentsSchema,
    grossGainCents: CentsSchema, // proceeds − cost base (negative uses reduced cost base → loss)
    isLoss: z.boolean(), discountEligible: z.boolean(), holdingDays: z.number().int(),
    method: z.enum(['discount', 'indexation', 'other']).prefault('other'),
    indexedCostBaseCents: CentsSchema.nullable().prefault(null),
    /** 2027 reform: how the pre-1-July-2027 part was measured (s112-155(3)); null/absent when not applicable. */
    valuation2027: z.enum(['market_value', 'apportionment']).nullable().optional(),
    financialYear: z.string(), // "2025-26"
    ...Timestamps,
});
export const CarriedForwardLossSchema = z.object({ entityId: IdSchema, financialYear: z.string(), lossCents: CentsSchema, source: z.enum(['computed', 'manual']).prefault('computed') });
export const WatchlistSchema = z.object({ id: IdSchema, userId: IdSchema, name: z.string(), securityIds: z.array(IdSchema).prefault([]), ...Timestamps });
export const PriceAlertSchema = z.object({ id: IdSchema, userId: IdSchema, securityId: IdSchema, condition: z.enum(['above', 'below', 'pct_change']), valueCents: CentsSchema.nullable(), pct: z.number().nullable(), isActive: z.boolean().prefault(true), triggeredAt: ISOTimestampSchema.nullable().prefault(null), ...Timestamps });
// ───────────────────────────── imports ─────────────────────────────
export const ImportKindSchema = z.enum(['bank_transactions', 'broker_trades', 'holdings_snapshot', 'prices', 'corporate_actions', 'income', 'budget', 'full_backup']);
export const ImportFormatSchema = z.enum(['csv', 'ofx', 'qfx', 'qif', 'camt053', 'json', 'xlsx', 'clipboard', 'pdf']);
export const ColumnMappingSchema = z.object({
    /** Keel field → source column header (or 0-based index as string). Fields: date, amount, debit, credit, payee, memo, reference, balance,
     *  code, exchange, side, units, price, brokerage, fees, gst, total, confirmationNo, settlementDate, currency, exDate, payDate, franked, unfranked, frankingCredits, type, param1..param4 */
    fields: z.record(z.string(), z.string()),
    dateFormat: z.string().prefault('dd/MM/yyyy'),
    decimalSeparator: z.enum(['.', ',']).prefault('.'),
    amountSign: z.enum(['signed', 'debit_credit_columns', 'debit_positive', 'type_column']).prefault('signed'),
    invertAmount: z.boolean().prefault(false),
    skipRows: z.number().int().prefault(0),
    hasHeader: z.boolean().prefault(true),
    sideValues: z.object({ buy: z.array(z.string()).prefault(['B', 'BUY', 'Buy']), sell: z.array(z.string()).prefault(['S', 'SELL', 'Sell']) }).prefault({}),
});
export const ImportProfileSchema = z.object({
    id: IdSchema, entityId: IdSchema.nullable().prefault(null), name: z.string(), preset: z.string().nullable().prefault(null), // 'nabtrade', 'cba', 'iress'...
    kind: ImportKindSchema, format: ImportFormatSchema, mapping: ColumnMappingSchema, defaultAccountId: IdSchema.nullable().prefault(null), ...Timestamps,
});
export const ImportBatchSchema = z.object({
    id: IdSchema, entityId: IdSchema, userId: IdSchema, kind: ImportKindSchema, format: ImportFormatSchema, preset: z.string().nullable(),
    fileName: z.string(), fileSha256: z.string(), accountId: IdSchema.nullable(),
    rowCount: z.number().int(), importedCount: z.number().int(), duplicateCount: z.number().int(), errorCount: z.number().int(),
    status: z.enum(['previewed', 'committed', 'reverted']), errors: z.array(z.object({ row: z.number().int(), message: z.string() })).prefault([]),
    createdAt: ISOTimestampSchema, committedAt: ISOTimestampSchema.nullable().prefault(null), revertedAt: ISOTimestampSchema.nullable().prefault(null),
    /** The kept original file (see SourceFile), once committed. */
    sourceFileId: IdSchema.nullable().optional(),
    /** Statement facts read from the file at preview (closing balance → a statement checkpoint on commit). */
    statement: z.object({
        balanceCents: z.number().int().nullable(), balanceDate: ISODateSchema.nullable(),
        openingBalanceCents: z.number().int().nullable().optional(), periodStart: ISODateSchema.nullable().optional(),
    }).nullable().optional(),
});
// ───────────────────────────── business-lite ─────────────────────────────
export const ContactSchema = z.object({ id: IdSchema, entityId: IdSchema, kind: z.enum(['customer', 'supplier', 'both']), name: z.string(), email: z.string().nullable().prefault(null), abn: z.string().nullable().prefault(null), address: z.string().prefault(''), paymentTermsDays: z.number().int().prefault(14), notes: z.string().prefault(''),
    countryCode: z.string().length(2).nullable().optional(), // ISO-3166 alpha-2, drives EC Sales List / reverse-charge eligibility
    vatNumber: z.string().nullable().optional(), // customer VAT registration (EU VAT number for EC Sales Lists)
    ...Timestamps });
export const InvoiceLineSchema = z.object({ id: IdSchema, description: z.string(), quantity: DecimalStringSchema.prefault('1'), unitPriceCents: CentsSchema, taxRate: z.number().prefault(0.10), categoryId: IdSchema.nullable().prefault(null), lineTotalCents: CentsSchema, taxCents: CentsSchema,
    reverseCharge: z.boolean().optional(), // UK domestic reverse charge (e.g. construction services): customer accounts for the VAT; shown in boxes 1 & 4, excluded from the invoice total
    ecSalesIndicator: z.union([z.literal(0), z.literal(2), z.literal(3)]).nullable().optional(), // EC Sales List: 0 goods, 2 triangulation, 3 services
});
export const InvoiceSchema = z.object({
    id: IdSchema, entityId: IdSchema, kind: z.enum(['invoice', 'bill']), contactId: IdSchema, number: z.string(), issueDate: ISODateSchema, dueDate: ISODateSchema,
    lines: z.array(InvoiceLineSchema).min(1), subtotalCents: CentsSchema, taxCents: CentsSchema, totalCents: CentsSchema, paidCents: CentsSchema.prefault(0),
    status: z.enum(['draft', 'sent', 'part_paid', 'paid', 'overdue', 'void']).prefault('draft'), paymentTransactionIds: z.array(IdSchema).prefault([]),
    currency: CurrencyCodeSchema, notes: z.string().prefault(''), ...Timestamps,
});
// ───────────────────────────── engagement ─────────────────────────────
export const CheckInSchema = z.object({ id: IdSchema, userId: IdSchema, entityId: IdSchema.nullable(), date: ISODateSchema, kind: z.enum(['daily', 'weekly_review', 'monthly_money_date']), reflections: z.record(z.string(), z.string()).prefault({}), durationSeconds: z.number().int().prefault(0), createdAt: ISOTimestampSchema });
export const StreakSchema = z.object({ userId: IdSchema, kind: z.enum(['daily', 'weekly_review']), current: z.number().int().prefault(0), longest: z.number().int().prefault(0), lastDate: ISODateSchema.nullable().prefault(null), freezeTokens: z.number().int().prefault(2), updatedAt: ISOTimestampSchema });
export const AchievementSchema = z.object({ id: IdSchema, userId: IdSchema, entityId: IdSchema.nullable(), key: z.string(), title: z.string(), description: z.string(), icon: z.string(), unlockedAt: ISOTimestampSchema, seenAt: ISOTimestampSchema.nullable().prefault(null) });
export const InsightSchema = z.object({
    id: IdSchema, userId: IdSchema, entityId: IdSchema.nullable(), kind: z.string(), // 'anomaly' | 'subscription_creep' | 'duplicate_charge' | 'dividend_received' | 'cgt_discount_unlocked' | 'budget_pacing' | 'fresh_start' | 'tax_loss_harvest' | 'goal_milestone' | 'price_alert' | 'low_balance_forecast'
    severity: z.enum(['info', 'positive', 'warning', 'action']).prefault('info'), title: z.string(), body: z.string(),
    data: z.record(z.string(), z.unknown()).prefault({}), actionRoute: z.string().nullable().prefault(null),
    createdAt: ISOTimestampSchema, dismissedAt: ISOTimestampSchema.nullable().prefault(null), expiresAt: ISOTimestampSchema.nullable().prefault(null),
});
// ───────────────────────────── card rewards (migration 0017) ─────────────────────────────
/** MCC code "5812" or inclusive range "5812-5814". */
export const MccSpecSchema = z.string().regex(/^\d{3,4}(-\d{3,4})?$/, 'MCC like 5812 or 5812-5814');
/** What a rule matches: any listed category (or a child of it), payee, MCC, or merchant-name fragment. Empty = nothing unless `everything`. */
export const EarnMatchSchema = z.object({
    categoryIds: z.array(IdSchema).max(200).optional(),
    merchantNames: z.array(z.string().trim().min(1).max(80)).max(200).optional(), // case-insensitive "contains" on the clean payee
    mcc: z.array(MccSpecSchema).max(100).optional(),
    payeeIds: z.array(IdSchema).max(200).optional(),
    everything: z.boolean().optional(), // all spend (flat-rate bonus with a cap)
});
export const EarnRuleSchema = z.object({
    label: z.string().trim().min(1).max(80),
    rate: z.number().min(0).max(100), // points per $1
    match: EarnMatchSchema,
    monthlyCapPoints: z.number().int().positive().nullable().optional(), // this rule's rate stops after N points a month (then base rate)
});
export const RewardsCardSchema = z.object({
    id: IdSchema,
    entityId: IdSchema,
    name: z.string().trim().min(1).max(120),
    issuerLabel: z.string().max(120).nullable().prefault(null),
    accountId: IdSchema.nullable().prefault(null), // the Mintage credit-card account it pays from
    annualFeeCents: CentsSchema.min(0).prefault(0),
    pointValueCents: z.number().min(0).max(100).prefault(0.5), // the user's own cents-per-point
    baseRate: z.number().min(0).max(100).prefault(1),
    monthlyBonusCapPoints: z.number().int().positive().nullable().prefault(null), // bonus rates until N points a month, then base rate
    monthlySpendCapCents: CentsSchema.positive().nullable().prefault(null), // no points at all on spend above this a month (e.g. Coles $8,000)
    rules: z.array(EarnRuleSchema).max(30).prefault([]), // first match wins
    notes: z.string().max(2000).prefault(''),
    templateId: z.string().max(60).nullable().prefault(null),
    ...Timestamps,
});
// ───────────────────────────── audit ─────────────────────────────
export const AuditLogSchema = z.object({ id: IdSchema, userId: IdSchema.nullable(), entityId: IdSchema.nullable(), action: z.string(), targetType: z.string(), targetId: IdSchema.nullable(), before: z.unknown().nullable().prefault(null), after: z.unknown().nullable().prefault(null), ip: z.string().nullable().prefault(null), createdAt: ISOTimestampSchema });
//# sourceMappingURL=types.js.map